import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthProvider';
import { apiUrl } from '../lib/api';

const MONO = "'JetBrains Mono', monospace";
const TEAL = '#00e5cc';
const AffiliateDashboard = () => {
  const { session } = useAuth();
  const [loading, setLoading] = useState(true);
  const [affiliate, setAffiliate] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [tierApplying, setTierApplying] = useState(false);
  const [tierMsg, setTierMsg] = useState('');
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const tick = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    const init = async () => {
      if (!session) { setError('not_logged_in'); setLoading(false); return; }
      const meRes = await fetch(apiUrl('/api/affiliates/me'), {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (meRes.status === 404) { setError('not_found'); setLoading(false); return; }
      if (!meRes.ok) { setError('fetch_failed'); setLoading(false); return; }
      const meJson = await meRes.json();
      const aff = meJson?.affiliate;
      if (!aff?.id) { setError('not_found'); setLoading(false); return; }
      setAffiliate(aff);
      const res = await fetch(apiUrl('/api/affiliates/dashboard/' + aff.id), {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const json = await res.json();
      if (!res.ok) { setError('fetch_failed'); setLoading(false); return; }
      setData(json);
      setLoading(false);
    };
    init();
  }, [session]);

  const copyLink = () => {
    navigator.clipboard.writeText('https://recallcore.ai/pricing?ref=' + data.affiliate.referral_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const applyTier2 = async () => {
    setTierApplying(true);
    setTierMsg('');
    const res = await fetch(apiUrl('/api/affiliates/apply-tier-upgrade'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ affiliate_id: affiliate.id }),
    });
    const json = await res.json();
    setTierMsg(res.ok ? 'Tier 2 application submitted.' : (json.error || 'Failed'));
    setTierApplying(false);
  };

  const badge = (text, color) => (
    <span style={{ padding: '3px 10px', background: color + '18', border: '1px solid ' + color + '55', borderRadius: '4px', fontSize: '10px', color, fontFamily: MONO, fontWeight: 700, letterSpacing: '0.1em' }}>{text}</span>
  );

  const HR = () => <div style={{ borderTop: '1px solid #0d1e35', margin: '28px 0', position: 'relative' }}><span style={{ position: 'absolute', top: '-7px', left: '0', fontSize: '10px', color: '#1a3050', fontFamily: MONO }}>──</span></div>;

  const sectionLabel = (text) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
      <span style={{ fontSize: '9px', color: TEAL, fontFamily: MONO, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 700 }}>▶ {text}</span>
      <div style={{ flex: 1, height: '1px', background: '#0d1e35' }} />
    </div>
  );

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#050810', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '11px', color: TEAL, letterSpacing: '0.2em', marginBottom: '8px' }}>INITIALIZING AGENT TERMINAL...</div>
        <div style={{ fontSize: '10px', color: '#1a3050' }}>Authenticating credentials</div>
      </div>
    </div>
  );

  if (error === 'not_logged_in') return (
    <div style={{ minHeight: '100vh', background: '#050810', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO }}>
      <div style={{ textAlign: 'center', padding: '40px', border: '1px solid #0d1e35', borderRadius: '12px', background: '#080c18' }}>
        <p style={{ fontSize: '10px', color: TEAL, letterSpacing: '0.2em', marginBottom: '16px' }}>ACCESS DENIED</p>
        <p style={{ color: '#4a6080', fontSize: '13px', marginBottom: '24px' }}>Authentication required to access agent terminal.</p>
        <a href='/user-login' style={{ color: TEAL, fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', textDecoration: 'none', border: '1px solid ' + TEAL + '44', padding: '8px 20px', borderRadius: '6px' }}>Login</a>
      </div>
    </div>
  );

  if (error === 'not_found') return (
    <div style={{ minHeight: '100vh', background: '#050810', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO }}>
      <div style={{ textAlign: 'center', padding: '40px', border: '1px solid #0d1e35', borderRadius: '12px', background: '#080c18' }}>
        <p style={{ fontSize: '10px', color: '#e24b4a', letterSpacing: '0.2em', marginBottom: '16px' }}>NO AGENT RECORD FOUND</p>
        <p style={{ color: '#4a6080', fontSize: '13px', marginBottom: '24px' }}>No affiliate account linked to this identity.</p>
        <a href='/affiliate-program/apply' style={{ color: TEAL, fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', textDecoration: 'none', border: '1px solid ' + TEAL + '44', padding: '8px 20px', borderRadius: '6px' }}>Apply Now</a>
      </div>
    </div>
  );

  if (error) return <div style={{ minHeight: '100vh', background: '#050810', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#e24b4a', fontFamily: MONO, fontSize: '13px' }}>SYSTEM ERROR — Failed to load terminal.</div>;

  const { affiliate: aff, commissions, referred_customers, payouts, stats } = data;
  const tierColor = aff.current_tier === 3 ? '#f0a500' : aff.current_tier === 2 ? '#00e5cc' : '#7a8090';
  const nextTierThreshold = aff.current_tier === 1 ? 100 : aff.current_tier === 2 ? 200 : null;
  const progress = nextTierThreshold ? Math.min(100, (parseFloat(stats.lifetime_approved) / nextTierThreshold) * 100) : 100;

  const statCard = (label, value, color) => (
    <div style={{ flex: '1 1 140px', padding: '20px', background: '#080c18', border: '1px solid #0d1e35', borderRadius: '8px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, width: '2px', height: '100%', background: color + '66' }} />
      <p style={{ fontSize: '9px', color: '#3a5570', letterSpacing: '0.2em', textTransform: 'uppercase', fontFamily: MONO, marginBottom: '10px', marginTop: 0 }}>{label}</p>
      <p style={{ fontSize: '20px', fontWeight: '700', color: color || '#c8d4e0', fontFamily: MONO, margin: 0, lineHeight: 1 }}>{value}</p>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#050810', color: '#c8d4e0', fontFamily: MONO }}>

      {/* TOP NAV */}
      <div style={{ borderBottom: '1px solid #0a1525', padding: '12px 32px', background: '#060a14' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <a href='/' style={{ color: TEAL, fontSize: '13px', textDecoration: 'none', fontWeight: 700, letterSpacing: '0.05em' }}>RECALLCORE</a>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <span style={{ fontSize: '10px', color: '#1a3050', letterSpacing: '0.1em' }}>AGENT TERMINAL v1.0</span>
            <span style={{ fontSize: '10px', color: '#1a3050' }}>{time.toLocaleTimeString()}</span>
          </div>
        </div>
      </div>

      {/* TERMINAL SHELL */}
      <div style={{ maxWidth: '900px', margin: '32px auto', padding: '0 24px 80px' }}>

        {/* TERMINAL HEADER BAR */}
        <div style={{ background: '#060a14', border: '1px solid #0d1e35', borderRadius: '10px 10px 0 0', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#e24b4a44', border: '1px solid #e24b4a66' }} />
          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#f0a50044', border: '1px solid #f0a50066' }} />
          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#00e5cc44', border: '1px solid #00e5cc66' }} />
          <span style={{ marginLeft: '12px', fontSize: '10px', color: '#1a3050', letterSpacing: '0.15em' }}>AFFILIATE_TERMINAL — {aff.referral_code}</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            {badge('TIER ' + aff.current_tier, tierColor)}
            {badge(aff.status.toUpperCase(), aff.status === 'approved' ? '#00e5cc' : '#e24b4a')}
          </div>
        </div>

        {/* TERMINAL BODY */}
        <div style={{ background: '#080c18', border: '1px solid #0d1e35', borderTop: 'none', borderRadius: '0 0 10px 10px', padding: '32px' }}>

          {/* AGENT IDENTITY */}
          <div style={{ marginBottom: '28px' }}>
            <p style={{ fontSize: '9px', color: '#1a4a3a', letterSpacing: '0.25em', textTransform: 'uppercase', marginBottom: '4px', marginTop: 0 }}>▸ AGENT IDENTITY</p>
            <h1 style={{ fontSize: '26px', fontWeight: '700', color: '#ffffff', margin: '0 0 4px', letterSpacing: '-0.02em' }}>{aff.full_name}</h1>
            <p style={{ fontSize: '11px', color: '#3a5570', margin: 0 }}>{aff.email}</p>
          </div>

          {aff.status === 'pending' && (
            <div style={{ padding: '12px 16px', background: '#0a1a10', border: '1px solid #00e5cc22', borderRadius: '6px', marginBottom: '24px' }}>
              <p style={{ fontSize: '11px', color: '#3a7060', fontFamily: MONO, margin: 0 }}>▸ APPLICATION PENDING — Under manual review. You will be contacted once approved.</p>
            </div>
          )}

          {/* REFERRAL LINK */}
          <div style={{ padding: '16px 20px', background: '#060a14', border: '1px solid #0d2a20', borderRadius: '8px', marginBottom: '28px' }}>
            <p style={{ fontSize: '9px', color: '#1a5040', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '10px', marginTop: 0 }}>▸ MISSION REFERRAL LINK</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <code style={{ fontSize: '12px', color: TEAL, flex: 1, minWidth: '200px', letterSpacing: '0.02em' }}>https://recallcore.ai/pricing?ref={aff.referral_code}</code>
              <button onClick={copyLink} style={{ padding: '7px 20px', background: copied ? '#0a2a20' : '#0d1e35', color: copied ? TEAL : '#7a9ab0', border: '1px solid ' + (copied ? TEAL + '66' : '#1a3050'), borderRadius: '5px', cursor: 'pointer', fontSize: '10px', fontFamily: MONO, fontWeight: 700, letterSpacing: '0.12em', transition: 'all 0.2s' }}>
                {copied ? '✓ COPIED' : 'COPY LINK'}
              </button>
            </div>
          </div>

          {/* STAT CARDS */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '28px' }}>
            {statCard('Pending', '$' + parseFloat(stats.pending_balance || 0).toFixed(2), '#f0a500')}
            {statCard('Available', '$' + parseFloat(stats.available_balance || 0).toFixed(2), '#00e5cc')}
            {statCard('Lifetime Earned', '$' + parseFloat(stats.lifetime_approved || 0).toFixed(2), '#c8d4e0')}
            {statCard('Paid Out', '$' + parseFloat(stats.paid_to_date || 0).toFixed(2), '#4ab080')}
            {statCard('Conversions', String(stats.total_conversions || 0), '#7a8090')}
          </div>

          {/* TIER PROGRESS */}
          {nextTierThreshold && (
            <div style={{ marginBottom: '28px', padding: '16px 20px', background: '#060a14', border: '1px solid #0d1e35', borderRadius: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <p style={{ fontSize: '9px', color: '#3a5570', letterSpacing: '0.2em', textTransform: 'uppercase', fontFamily: MONO, margin: 0 }}>▸ PROGRESS TO TIER {aff.current_tier + 1}</p>
                <p style={{ fontSize: '11px', color: '#4a6080', fontFamily: MONO, margin: 0 }}>${parseFloat(stats.lifetime_approved || 0).toFixed(0)} / ${nextTierThreshold}</p>
              </div>
              <div style={{ height: '3px', background: '#0d1e35', borderRadius: '2px' }}>
                <div style={{ height: '3px', background: 'linear-gradient(90deg, ' + TEAL + '88, ' + TEAL + ')', borderRadius: '2px', width: progress + '%', transition: 'width 0.8s ease', boxShadow: '0 0 8px ' + TEAL + '44' }} />
              </div>
              {aff.current_tier === 1 && parseFloat(stats.lifetime_approved || 0) >= 100 && (
                <div style={{ marginTop: '14px' }}>
                  <button onClick={applyTier2} disabled={tierApplying} style={{ padding: '8px 20px', background: '#0a1a30', color: TEAL, border: '1px solid ' + TEAL + '44', borderRadius: '5px', cursor: 'pointer', fontSize: '10px', fontFamily: MONO, fontWeight: 700, letterSpacing: '0.12em' }}>
                    {tierApplying ? 'PROCESSING...' : '▸ APPLY FOR TIER 2'}
                  </button>
                  {tierMsg && <p style={{ fontSize: '11px', color: TEAL, marginTop: '8px', fontFamily: MONO }}>{tierMsg}</p>}
                </div>
              )}
            </div>
          )}

          <HR />

          {/* COMMISSIONS */}
          {sectionLabel('Commission Log')}
          {commissions.length === 0 ? (
            <p style={{ fontSize: '12px', color: '#2a4060', fontFamily: MONO, marginBottom: '28px' }}>No commissions recorded yet.</p>
          ) : (
            <div style={{ overflowX: 'auto', marginBottom: '28px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', fontFamily: MONO }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #0d1e35' }}>
                    {['Date', 'Amount', 'Rate', 'Status', 'Month'].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#3a5570', fontWeight: 600, fontSize: '9px', letterSpacing: '0.15em', textTransform: 'uppercase' }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {commissions.map(c => (
                    <tr key={c.id} style={{ borderBottom: '1px solid #08111a' }}>
                      <td style={{ padding: '10px 12px', color: '#4a6080' }}>{new Date(c.created_at).toLocaleDateString()}</td>
                      <td style={{ padding: '10px 12px', color: '#c8d4e0', fontWeight: 700 }}>${parseFloat(c.commission_amount_usd).toFixed(2)}</td>
                      <td style={{ padding: '10px 12px', color: '#4a6080' }}>{c.commission_rate}%</td>
                      <td style={{ padding: '10px 12px' }}>{badge(c.status.toUpperCase(), c.status === 'approved' || c.status === 'paid' ? '#00e5cc' : c.status === 'pending' ? '#f0a500' : '#e24b4a')}</td>
                      <td style={{ padding: '10px 12px', color: '#4a6080' }}>M{c.commission_month || c.month_number || 1}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <HR />

          {/* REFERRED CUSTOMERS */}
          {sectionLabel('Referred Operatives')}
          {referred_customers.length === 0 ? (
            <p style={{ fontSize: '12px', color: '#2a4060', fontFamily: MONO, marginBottom: '28px' }}>No referred customers yet. Share your link to start earning.</p>
          ) : (
            <div style={{ overflowX: 'auto', marginBottom: '28px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', fontFamily: MONO }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #0d1e35' }}>
                    {['Email', 'Status', 'First Click', 'Converted'].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#3a5570', fontWeight: 600, fontSize: '9px', letterSpacing: '0.15em', textTransform: 'uppercase' }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {referred_customers.map(r => (
                    <tr key={r.id} style={{ borderBottom: '1px solid #08111a' }}>
                      <td style={{ padding: '10px 12px', color: '#5a7090' }}>{r.email || '—'}</td>
                      <td style={{ padding: '10px 12px' }}>{badge(r.status.toUpperCase(), r.status === 'converted' ? '#00e5cc' : r.status === 'attributed' ? '#f0a500' : '#4a6080')}</td>
                      <td style={{ padding: '10px 12px', color: '#4a6080' }}>{r.first_click_at ? new Date(r.first_click_at).toLocaleDateString() : '—'}</td>
                      <td style={{ padding: '10px 12px', color: '#4a6080' }}>{r.first_paid_conversion_at ? new Date(r.first_paid_conversion_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <HR />

          {/* PAYOUT HISTORY */}
          {sectionLabel('Payout History')}
          {payouts.length === 0 ? (
            <p style={{ fontSize: '12px', color: '#2a4060', fontFamily: MONO }}>No payouts yet. Minimum threshold: $100 available balance.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', fontFamily: MONO }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #0d1e35' }}>
                    {['Date', 'Amount', 'Method', 'Status'].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#3a5570', fontWeight: 600, fontSize: '9px', letterSpacing: '0.15em', textTransform: 'uppercase' }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {payouts.map(p => (
                    <tr key={p.id} style={{ borderBottom: '1px solid #08111a' }}>
                      <td style={{ padding: '10px 12px', color: '#4a6080' }}>{new Date(p.created_at).toLocaleDateString()}</td>
                      <td style={{ padding: '10px 12px', color: '#c8d4e0', fontWeight: 700 }}>${parseFloat(p.amount).toFixed(2)}</td>
                      <td style={{ padding: '10px 12px', color: '#4a6080' }}>{p.payout_method || '—'}</td>
                      <td style={{ padding: '10px 12px' }}>{badge(p.status.toUpperCase(), p.status === 'paid' ? '#00e5cc' : '#f0a500')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        </div>
      </div>

      <div style={{ borderTop: '1px solid #0a1525', padding: '16px 32px', textAlign: 'center' }}>
        <p style={{ fontSize: '10px', color: '#1a2e48', margin: 0, fontFamily: MONO, letterSpacing: '0.08em' }}>© 2026 RECALLCORE · CODEX MULTIVERSE LIMITED · AGENT NETWORK</p>
      </div>
    </div>
  );
};

export default AffiliateDashboard;
